import React from 'react';
import { Camera, MessageSquare, TrendingUp, TrendingDown } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { MOOD_EMOJI } from '../ClientCheckInsSheet';

function DesktopRow({ ci, prevCi, isSelected, onSelect, rowRefs }) {
  const wDiff = ci.weight_kg && prevCi?.weight_kg ? (ci.weight_kg - prevCi.weight_kg) : null;
  return (
    <button
      type="button"
      ref={el => { if (rowRefs) rowRefs.current[`desktop:${ci.id}`] = el; }}
      onClick={() => onSelect(ci.id)}
      data-testid={`checkin-list-row-${ci.id}`}
      className={`w-full text-left flex items-center gap-3 p-3 border-l-2 transition-colors ${
        isSelected ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-muted/40'
      }`}
    >
      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-muted/60 flex flex-col items-center justify-center">
        <span className="text-[10px] font-bold leading-none">{ci.date?.slice(8, 10)}</span>
        <span className="text-[8px] text-muted-foreground uppercase">{ci.date ? format(new Date(ci.date), 'MMM') : ''}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          {ci.weight_kg && <span className="text-xs font-bold font-mono">{ci.weight_kg}kg</span>}
          {wDiff !== null && (
            <span className={`text-[10px] font-semibold flex items-center gap-0.5 ${wDiff < 0 ? 'text-green-500' : 'text-red-400'}`}>
              {wDiff < 0 ? <TrendingDown className="h-2.5 w-2.5" /> : <TrendingUp className="h-2.5 w-2.5" />}
              {wDiff > 0 ? '+' : ''}{wDiff.toFixed(1)}
            </span>
          )}
          {ci.mood && <span className="text-xs">{MOOD_EMOJI[ci.mood]}</span>}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          {ci.has_photos && <Camera className="h-2.5 w-2.5 text-muted-foreground" />}
          {(ci.coach_notes || ci.coach_feedback) && <MessageSquare className="h-2.5 w-2.5 text-primary" />}
          <span className="text-[10px] text-muted-foreground ml-auto">
            {formatDistanceToNow(new Date(ci.date), { addSuffix: true })}
          </span>
        </div>
      </div>
    </button>
  );
}

export default function CheckInList({ checkIns, selectedId, onSelect, variant, rowRefs }) {
  if (!checkIns?.length) return null;

  if (variant === 'desktop') {
    return (
      <div className="divide-y divide-border/40">
        {checkIns.map((ci, idx) => (
          <DesktopRow
            key={ci.id}
            ci={ci}
            prevCi={checkIns[idx + 1]}
            isSelected={selectedId === ci.id}
            onSelect={onSelect}
            rowRefs={rowRefs}
          />
        ))}
      </div>
    );
  }

  if (variant === 'mobile') {
    return (
      <div className="flex overflow-x-auto snap-x snap-mandatory gap-2 px-4 py-2 no-scrollbar">
        {checkIns.map((ci, idx) => {
          const prevCi = checkIns[idx + 1];
          const wDiff = ci.weight_kg && prevCi?.weight_kg ? (ci.weight_kg - prevCi.weight_kg) : null;
          const isSelected = selectedId === ci.id;
          return (
            <button
              key={ci.id}
              type="button"
              ref={el => { if (rowRefs) rowRefs.current[`mobile:${ci.id}`] = el; }}
              onClick={() => onSelect(ci.id)}
              data-testid={`checkin-chip-${ci.id}`}
              className={`flex-shrink-0 min-w-[112px] h-16 rounded-xl border snap-start px-3 py-2 text-left transition-colors ${
                isSelected
                  ? 'border-primary bg-primary/10'
                  : 'border-border/50 bg-muted/30'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase text-muted-foreground">
                  {ci.date ? format(new Date(ci.date), 'dd MMM') : ''}
                </span>
                {ci.mood && <span className="text-xs">{MOOD_EMOJI[ci.mood]}</span>}
              </div>
              <div className="mt-0.5 flex items-center gap-1">
                {ci.weight_kg != null && <span className="text-xs font-bold font-mono">{ci.weight_kg}kg</span>}
                {wDiff !== null && (
                  <span className={`text-[10px] font-semibold ${wDiff < 0 ? 'text-green-500' : 'text-red-400'}`}>
                    {wDiff > 0 ? '+' : ''}{wDiff.toFixed(1)}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    );
  }

  return null;
}
